import React, { useEffect, useRef, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import chroma from "chroma-js";
import { ColorPicker as ReactColorPicker, Color } from "react-color-gradient";

import { Popover, POPOVER_POSITIONS } from "../../feedback/popover/index.jsx";
import { Menu, MENU_SIZES, MenuItem } from "../dropdown/menu.jsx";
import { SimpleDropdown } from "../index.jsx";
import { SingleLine, SINGLELINE_SIZES } from "../singleline/index.jsx";

import styles from "./color.css";

export function ColorPicker({ value, onChange }) {
  const [color, setColor] = useState(() => Color.fromCSS(value));

  function handleChange(color) {
    setColor(color);
    onChange(color.hex);
  }

  return (
    <Popover
      arrowPointAtCenter={false}
      targetRenderer={() => {
        return (
          <div
            className={styles.colorPopover}
            style={{
              backgroundColor: color.hex
            }}
          />
        );
      }}
      position={POPOVER_POSITIONS.RIGHT}
    >
      <ReactColorPicker value={color} onChange={handleChange} />
    </Popover>
  );
}

ColorPicker.propTypes = {
  /**
   * select color value for colorPicker
   */
  value: PropTypes.string.isRequired,
  /**
   * onChange func will trigger when user changes any color in ColorPicker
   */
  onChange: PropTypes.func
};

const COLOR_MODES = ["Hex code", "RGBA"];

export function ColorPanel({ value, onChange }) {
  const [color, setColor] = useState(value ? chroma(value) : "#0565ff");
  const [mode, setMode] = useState(COLOR_MODES[0]);
  const [alpha, setAlpha] = useState(value ? chroma(value).alpha() * 100 : 100);
  return (
    <div className={styles.colorpanel}>
      <ColorCanvas
        color={color}
        onChange={(color) => {
          setColor(color);
          onChange(color.hex());
        }}
      />
      <div className={styles.colorsliders}>
        <Slider
          background={`linear-gradient(to right,
            rgb(255, 0, 0) 0%,
            rgb(255, 255, 0) 17%,
            rgb(0, 255, 0) 33%,
            rgb(0, 255, 255) 50%,
            rgb(0, 0, 255) 67%,
            rgb(255, 0, 255) 83%,
            rgb(255, 0, 0) 100%)`}
          value={color.hsv()[0] / 360}
          onChange={(value) => {
            setColor(color.set("hsv.h", value * 360));
            onChange(color.set("hsv.h", value * 360).hex());
          }}
          color={color}
        />
        <div
          className={styles.colorpreview}
          style={{ background: color.hex() }}
        />
        <Slider
          background={`linear-gradient(to right, rgba(144, 25, 27, 0) 0%, ${color
            .hex()
            .slice(0, 7)} 100%), conic-gradient(
            #fff 0.25turn,
            #D4D3D3 0.25turn 0.5turn,
            #fff 0.5turn 0.75turn,
            #D4D3D3 0.75turn
          ) top left / 4% 100% repeat`}
          value={color.alpha()}
          onChange={(value) => {
            setAlpha(Math.round(value * 100));
            setColor((color) => {
              onChange(color.alpha(value, false).hex());
              return color.alpha(value, false);
            });
          }}
          color={color}
        />
      </div>

      <div className={styles.modeSelector}>
        <SimpleDropdown
          value={mode}
          paddingNone={true}
          listRenderer={({ closePopup }) => {
            return (
              <Menu size={MENU_SIZES.TINY} selection={mode}>
                {COLOR_MODES.map((mode) => {
                  return (
                    <MenuItem
                      key={mode}
                      onClick={() => {
                        setMode(mode);
                        closePopup();
                      }}
                    >
                      {mode}
                    </MenuItem>
                  );
                })}
              </Menu>
            );
          }}
        />
      </div>

      <div className={styles.colorinput}>
        {mode === COLOR_MODES[0] && (
          <HexInput
            color={color}
            onChange={(color) => {
              setColor(color);
              onChange(color.hex());
            }}
          />
        )}
        {mode === COLOR_MODES[1] && (
          <RGBInput
            color={color}
            onChange={(color) => {
              setColor(color);
              onChange(color.hex());
            }}
          />
        )}
        <input
          type="number"
          className={styles.alpha}
          min={0}
          max={100}
          value={alpha}
          onChange={(e) => {
            const newColor = color.alpha(Number(e.target.value) / 100);
            setAlpha(Number(e.target.value));
            setColor(newColor);
            onChange(newColor.hex());
          }}
          size={SINGLELINE_SIZES.SMALL}
        />{" "}
        <span className={`small-400 ${styles.colorPercentage}`}>%</span>
      </div>
    </div>
  );
}

ColorPanel.propTypes = {
  /**
   * select color value for colorPicker
   */
  value: PropTypes.string.isRequired,
  /**
   * onChange func will trigger when user changes any color in ColorPicker
   */
  onChange: PropTypes.func
};

/**
 *
 * @param {{ color: import("chroma-js").Color }}
 */
function HexInput({ color, onChange }) {
  const [hex, setHex] = useState(color.hex().slice(0, 9));
  useEffect(
    function update() {
      setHex(color.hex().slice(0, 9));
    },
    [color]
  );

  function updateValue() {
    if (chroma.valid(hex)) {
      const newColor = chroma(hex).alpha(chroma(hex).alpha());
      onChange(newColor);
    } else {
      setHex(color.hex().slice(0, 7));
    }
  }
  return (
    <SingleLine
      value={hex}
      onChange={(value) => {
        setHex(value);
      }}
      onBlur={updateValue}
      className={styles.hex}
      size={SINGLELINE_SIZES.SMALL}
      onEnter={updateValue}
    />
  );
}
HexInput.propTypes = {
  color: chroma,
  onChange: PropTypes.func
};

/**
 *
 * @param {{ color: import("chroma-js").Color }}
 */
function RGBInput({ color, onChange }) {
  const [r, setR] = useState(color.rgb()[0]);
  const [g, setG] = useState(color.rgb()[1]);
  const [b, setB] = useState(color.rgb()[2]);
  const rgb = `rgb(${r}, ${g}, ${b})`;
  useEffect(
    function updateColor() {
      setR(color.rgb()[0]);
      setG(color.rgb()[1]);
      setB(color.rgb()[2]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...color.rgb()]
  );
  return (
    <>
      <div className={styles.rgb}>
        <input
          type="number"
          value={r}
          onChange={(e) => setR(e.target.value)}
          onBlur={() => {
            if (r >= 0 && r <= 255) {
              const newColor = chroma(rgb).alpha(color.alpha());
              onChange(newColor);
            } else {
              setR(color.rgb()[0]);
            }
          }}
          min={0}
          max={255}
        />
        <span className={"micro"}>{"R"}</span>
      </div>
      <div className={styles.rgb}>
        <input
          type="number"
          value={g}
          onChange={(e) => setG(e.target.value)}
          onBlur={() => {
            if (g >= 0 && g <= 255) {
              const newColor = chroma(rgb).alpha(color.alpha());
              onChange(newColor);
            } else {
              setR(color.rgb()[1]);
            }
          }}
          min={0}
          max={255}
        />
        <span className={"micro"}>{"G"}</span>
      </div>
      <div className={styles.rgb}>
        <input
          type="number"
          value={b}
          onChange={(e) => setB(e.target.value)}
          onBlur={() => {
            if (b >= 0 && b <= 255) {
              const newColor = chroma(rgb).alpha(color.alpha());
              onChange(newColor);
            } else {
              setB(color.rgb()[2]);
            }
          }}
          min={0}
          max={255}
        />
        <span className={"micro"}>{"B"}</span>
      </div>
    </>
  );
}
RGBInput.propTypes = {
  color: chroma,
  onChange: PropTypes.func
};

/**
 *
 * @param {{ color: import("chroma-js").Color }}
 */

function ColorCanvas({ color, onChange }) {
  const sliderRef = useRef();
  const canvasRef = useRef();
  const colorRef = useRef(color);

  useEffect(
    function colorUpdate() {
      colorRef.current = color;
      const hsv = color.hsv();
      sliderRef.current.style.top = `${
        (1 - hsv[2]) * canvasRef.current.clientHeight
      }px`;
      sliderRef.current.style.left = `${
        hsv[1] * canvasRef.current.clientWidth
      }px`;
    },
    [color]
  );

  useEffect(function handlePointerEvents() {
    function pointerMove(e) {
      let offsetX = e.offsetX;
      let offsetY = e.offsetY;
      if (offsetX < 0) offsetX = 0;
      if (offsetX > e.target.clientWidth) offsetX = e.target.clientWidth;
      if (offsetY < 0) offsetY = 0;
      if (offsetY > e.target.clientHeight) offsetY = e.target.clientHeight;

      sliderRef.current.style.top = `${offsetY}px`;
      sliderRef.current.style.left = `${offsetX}px`;

      const s = offsetX / e.target.clientWidth;
      const v = 1 - offsetY / e.target.clientHeight;

      // console.log("IN HEX", colorRef.current.hex());
      let hsv = colorRef.current.hsv();
      if (isNaN(hsv[0])) hsv[0] = 0;
      if (hsv[0] !== undefined && s !== undefined && v !== undefined) {
        onChange(chroma.hsv(hsv[0], s, v));
      }
    }

    function pointerUp(e) {
      canvasRef.current.releasePointerCapture(e.pointerId);
      canvasRef.current.removeEventListener("pointermove", pointerMove);
    }

    function pointerDown(e) {
      canvasRef.current.addEventListener("pointermove", pointerMove);
      canvasRef.current.setPointerCapture(e.pointerId);
      pointerMove(e);
    }

    if (canvasRef.current) {
      canvasRef.current.addEventListener("pointerdown", pointerDown);
      canvasRef.current.addEventListener("pointerup", pointerUp);
      const hsv = color.hsv();
      sliderRef.current.style.top = `${
        (1 - hsv[2]) * canvasRef.current.clientHeight
      }px`;
      sliderRef.current.style.left = `${
        hsv[1] * canvasRef.current.clientWidth
      }px`;
    }

    return () => {
      canvasRef?.current?.removeEventListener("pointerup", pointerUp);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      canvasRef?.current?.removeEventListener("pointerdown", pointerDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      className={styles.colorcanvas}
      style={{ background: `hsl(${color.hsl()[0]}, 100%, 50%)` }}
      ref={canvasRef}
    >
      <div className={styles.colorcanvasoverlay} />
      <div
        className={styles.canvasslider}
        ref={sliderRef}
        style={{
          background: color.hex()
        }}
      />
    </div>
  );
}

ColorCanvas.propTypes = {
  color: chroma,
  onChange: PropTypes.func
};

function Slider({ background, value, color, onChange }) {
  const sliderRef = useRef();
  const thumbRef = useRef();

  useEffect(
    function updatePos() {
      thumbRef.current.style.left = `${
        value * sliderRef.current.clientWidth
      }px`;
    },
    [value]
  );

  useEffect(function pointerHandlers() {
    function pointerMove(e) {
      let offset = e.offsetX;
      if (offset < 0) offset = 0;
      if (offset > e.target.clientWidth) offset = e.target.clientWidth;
      thumbRef.current.style.left = `${offset}px`;
      onChange(offset / e.target.clientWidth);
    }

    function pointerDown(e) {
      sliderRef.current.addEventListener("pointermove", pointerMove);
      sliderRef.current.setPointerCapture(e.pointerId);
      pointerMove(e);
    }

    function pointerUp(e) {
      sliderRef.current.releasePointerCapture(e.pointerId);
      sliderRef.current.removeEventListener("pointermove", pointerMove);
    }

    if (sliderRef.current) {
      sliderRef.current.addEventListener("pointerdown", pointerDown);
      sliderRef.current.addEventListener("pointerup", pointerUp);
      thumbRef.current.style.left = `${
        value * sliderRef.current.clientWidth
      }px`;
    }

    return () => {
      sliderRef?.current?.removeEventListener("pointerdown", pointerDown);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      sliderRef?.current?.removeEventListener("pointerup", pointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={styles.slider} style={{ background }} ref={sliderRef}>
      <div
        className={styles.thumb}
        ref={thumbRef}
        style={{
          background: color.hex().slice(0, 7)
        }}
      />
    </div>
  );
}
Slider.propTypes = {
  background: PropTypes.string,
  value: PropTypes.string.isRequired,
  color: chroma,
  onChange: PropTypes.func
};

export { ColorPalette as ColorPalettePicker } from "./palette/index.jsx";
export { ColorItem, COLOR_ITEM_SIZES } from "./palette/item.jsx";
